import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mapeo de rutas a títulos legibles
const PAGE_TITLES = {
  '/': 'Login',
  '/admin': 'Dashboard Admin',
  '/admin/clients': 'Gestión de Clientes',
  '/admin/plans': 'Planes de Entrenamiento',
  '/admin/plans/new': 'Crear Nuevo Plan',
  '/admin/exercises': 'Biblioteca de Ejercicios',
  '/admin/payments': 'Pagos y Facturación',
  '/admin/tips': 'Píldoras del Día',
  '/admin/recursos': 'Centro de Recursos',
  '/admin/access-control': 'Control de Accesos',
  '/admin-pin': 'Verificación PIN',
  '/portal': 'Mi Entrenamiento',
  '/portal/session': 'Sesión de Hoy',
  '/portal/recursos': 'Centro de Recursos',
};

const getPageTitle = (pathname) => {
  // Buscar coincidencia exacta primero
  if (PAGE_TITLES[pathname]) {
    return PAGE_TITLES[pathname];
  }
  
  // Buscar coincidencias parciales
  if (pathname.startsWith('/admin/clients/')) {
    return 'Detalle de Cliente';
  }
  if (pathname.startsWith('/admin/plans/')) {
    return 'Editor de Plan';
  }
  if (pathname.startsWith('/portal/plan/')) {
    return 'Ver Plan';
  }
  
  return pathname;
};

// Generar un ID de sesión único
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('tracking_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('tracking_session_id', sessionId);
  }
  return sessionId;
};

const AccessTracker = () => {
  const location = useLocation();
  const { user, token } = useAuth();
  const lastPathRef = useRef(null);

  useEffect(() => {
    // Solo trackear si hay usuario logueado y token
    if (!user || !token) return;
    
    // Evitar trackear la misma página dos veces seguidas
    if (lastPathRef.current === location.pathname) return;
    lastPathRef.current = location.pathname;

    const logAccess = async () => {
      try {
        await axios.post(`${API}/access-log`, {
          page: location.pathname,
          page_title: getPageTitle(location.pathname),
          action: 'view',
          session_id: getSessionId()
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        // Silenciar errores de tracking para no afectar la UX
        console.debug('Access log error:', error);
      }
    };

    logAccess();
  }, [location.pathname, user, token]);

  // Este componente no renderiza nada
  return null;
};

// Función para trackear acciones específicas (clicks, etc.)
export const trackAction = async (action, details, token) => {
  if (!token) return;
  
  try {
    await axios.post(`${API}/access-log`, {
      page: window.location.pathname,
      page_title: getPageTitle(window.location.pathname),
      action: action,
      details: details,
      session_id: getSessionId()
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch (error) {
    console.debug('Track action error:', error);
  }
};

// Función para trackear login
export const trackLogin = async (token) => {
  if (!token) return;
  
  try {
    await axios.post(`${API}/access-log`, {
      page: '/login',
      page_title: 'Login',
      action: 'login',
      details: 'Usuario inició sesión',
      session_id: getSessionId()
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch (error) {
    console.debug('Track login error:', error);
  }
};

export default AccessTracker;
